import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from './IconButton';
import { IconEdit, IconTrash } from './icons';
import { Modal } from './Modal';
import { useConfig, labelFor, listOptions } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';

interface PositionOption {
  id: number;
  position_code: string;
  position_title: string;
}

interface LeaveEntitlementPolicy {
  id: number;
  position_id: number;
  position: PositionOption | null;
  leave_type: string;
  annual_days: string;
  pro_rata: boolean;
  approval_levels: number;
}

interface PolicyForm {
  position_id: string | number;
  leave_type: string;
  annual_days: string;
  pro_rata: boolean;
  approval_levels: string;
}

const emptyForm: PolicyForm = { position_id: '', leave_type: '', annual_days: '', pro_rata: true, approval_levels: '1' };

interface LeaveEntitlementsPanelProps {
  // GET /leave-entitlement-policies only needs hr.view, but create/update/
  // delete need settings.manage — this panel is shared by the Leave page
  // (view for any hr.view holder) and the System settings page (always
  // manage-capable, since its own route already requires settings.manage).
  canManage: boolean;
}

export function LeaveEntitlementsPanel({ canManage }: LeaveEntitlementsPanelProps) {
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<LeaveEntitlementPolicy[] | null>(null);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [editing, setEditing] = useState<'new' | LeaveEntitlementPolicy | null>(null);
  const [form, setForm] = useState<PolicyForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<LeaveEntitlementPolicy[]>('/leave-entitlement-policies').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    if (canManage) api.get<PositionOption[]>('/positions').then((res) => setPositions(res.data));
  }, [canManage]);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: LeaveEntitlementPolicy) => {
    setForm({
      position_id: item.position_id,
      leave_type: item.leave_type,
      annual_days: item.annual_days,
      pro_rata: item.pro_rata,
      approval_levels: String(item.approval_levels),
    });
    setErrors({});
    setEditing(item);
  };

  const set =
    (key: keyof PolicyForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/leave-entitlement-policies', form);
      else if (editing) await api.put(`/leave-entitlement-policies/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: LeaveEntitlementPolicy) => {
    if (
      !(await confirmDelete(
        `Delete the entitlement policy for "${item.position?.position_title}" / ${labelFor(lists, 'leave_type', item.leave_type)}?`
      ))
    )
      return;
    try {
      await api.delete(`/leave-entitlement-policies/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      {canManage && (
        <div className="toolbar">
          <button type="button" className="btn btn-primary" onClick={openNew}>
            + Add
          </button>
        </div>
      )}

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th>Leave Type</th>
                <th>Annual Days</th>
                <th>Pro-Rata</th>
                <th>Approval Levels</th>
                {canManage && <th></th>}
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.position?.position_title ?? '—'}</td>
                  <td>{labelFor(lists, 'leave_type', item.leave_type)}</td>
                  <td className="muted">{item.annual_days}</td>
                  <td className="muted">{item.pro_rata ? 'Yes' : 'No'}</td>
                  <td className="muted">{item.approval_levels === 0 ? 'None (auto-approved)' : item.approval_levels}</td>
                  {canManage && (
                    <td className="right">
                      <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                    </td>
                  )}
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 6 : 5} className="muted center">
                    No leave entitlement policies yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {canManage && editing && (
        <Modal title={editing === 'new' ? 'Add leave entitlement policy' : 'Edit leave entitlement policy'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Position *
              <select value={form.position_id} onChange={set('position_id')} required>
                <option value="">Select…</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.position_code} — {p.position_title}
                  </option>
                ))}
              </select>
              {errors.position_id && <small className="error">{errors.position_id[0]}</small>}
            </label>
            <label className="span-2">
              Leave Type *
              <select value={form.leave_type} onChange={set('leave_type')} required>
                <option value="">Select…</option>
                {listOptions(lists, 'leave_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.leave_type && <small className="error">{errors.leave_type[0]}</small>}
            </label>
            <label className="span-2">
              Annual Days *
              <input type="number" step="0.5" min="0" value={form.annual_days} onChange={set('annual_days')} required />
              {errors.annual_days && <small className="error">{errors.annual_days[0]}</small>}
            </label>
            <label className="checkbox-inline span-2">
              <input
                type="checkbox"
                checked={form.pro_rata}
                onChange={(e) => setForm({ ...form, pro_rata: e.target.checked })}
              />
              Pro-rate a mid-year starter's first-year entitlement
            </label>
            <small className="muted span-2">
              When checked, an employee hired partway through a calendar year gets that year's Annual Days scaled down to the
              portion of the year remaining from their hire date. Every later year is unaffected.
            </small>
            {errors.pro_rata && <small className="error span-2">{errors.pro_rata[0]}</small>}
            <label className="span-2">
              Workflow Approval Levels *
              <input type="number" step="1" min="0" max="10" value={form.approval_levels} onChange={set('approval_levels')} required />
              <small className="muted">
                How many managers must approve, in order — 1: manager, 2: manager's manager, etc. 0 means no approval is
                required; the request is approved automatically.
              </small>
              {errors.approval_levels && <small className="error">{errors.approval_levels[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
